import { ArticleType, LenderMap, Product, Program } from '@nemuru-eng/js-api-client';
import { z } from 'zod';

import { authenticationTypeMap } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/maps/authenticationTypeMap';
import { getCredentialFieldsConfig } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/maps/credentialFieldConfigMap';
import { AuthenticationType } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/types/AuthenticationType';
import { GetTextIdTranslationType } from '@/components/shared/hooks/useTranslation';
import { requiredEnum, requiredString } from '@/utils/zodUtils';

export const AddAgentInLenderContextCredentialsFormSchema = (t: GetTextIdTranslationType) =>
  z
    .object({
      lenderId: requiredString(t),
      product: requiredEnum(Product, t),
      program: requiredEnum(Program, t),
      merchantIdInLenderContext: z.string().nullable().optional(),
      channelIdInLenderContext: z.string().nullable().optional(),
      agentIdInLenderContext: z.string().nullable().optional(),
      market: z.string().nullable().optional(),
      username: z.string().nullable().optional(),
      password: z.string().nullable().optional(),
      clientSecret: z.string().nullable().optional(),
      clientId: z.string().nullable().optional(),
      articleType: requiredEnum(ArticleType).nullable().optional(),
    })
    .superRefine((data, ctx) => {
      const lenderName = LenderMap.fromLenderId(data.lenderId);
      const fieldConfigs = lenderName
        ? getCredentialFieldsConfig(lenderName, data.product as Product, data.program as Program)
        : undefined;

      if (fieldConfigs) {
        for (const config of fieldConfigs) {
          if (config.required && !data[config.platformField]) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('REQUIRED_FIELD'),
              path: [config.platformField],
            });
          }
        }
      } else {
        // Fallback: legacy auth-type-based validation for unconfigured lenders
        // TODO: Remove once all lenders are in credentialFieldConfigMap
        const authenticationType = lenderName ? authenticationTypeMap.get(lenderName) : undefined;
        if (authenticationType === AuthenticationType.PASSWORD && !data.password) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('REQUIRED_FIELD'),
            path: ['password'],
          });
        }
        if (authenticationType === AuthenticationType.OAUTH_2 && !data.clientId) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('REQUIRED_FIELD'),
            path: ['clientId'],
          });
        }
        if (!data.merchantIdInLenderContext) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('REQUIRED_FIELD'),
            path: ['merchantIdInLenderContext'],
          });
        }
      }
    });

export type AddAgentInLenderContextCredentialsFormType = z.infer<
  ReturnType<typeof AddAgentInLenderContextCredentialsFormSchema>
>;
